import React, { useEffect, useMemo, useState } from "react";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleSelect from "../../components/selects/simpleSelect";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import FormTextField from "../../components/textField/formTextField";
import CharacterCounter from "../../components/characterCounter";
import LaddaButton from "../../components/buttons/laddaButton";
import { canManageInterestedPartiesProcessRecords } from "../../utils/interestedPartiesPermissions";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import VerificationModal from "../../components/modals/verificationModal";
import ToastNotification from "../../components/toastNotification";
import {
  stakeholderlevel,
  stakeholderRelationselect,
  stakeholdersEntries,
  stakeholdersinfluence,
  stakeholdersRolType,
  YesNoOptionsGroup
} from "../../constants/selectData";
import FormCalendar from "../../components/calendar/formCalendar";
import FormRadioButton from "../../components/formRadioButton";
import {
  callApiAsync,
  postCreateInterestedParties
} from "../../services/apiService";
import { StyledTooltip } from "../../components/toolltipComponent";
import { icons } from "../../constants";

const validationSchema = yup.object().shape({
  enter: yup.string().required("La entrada es obligatoria"),
  interested_parties: yup.string().required("El nombre es obligatorio"),
  rol_type: yup.string().required("El tipo de rol es obligatorio"),
  relation_type: yup.string().required("El tipo de relación es obligatorio"),
  influence_type: yup.string().required("El tipo de influencia es obligatorio"),
  influence_level: yup
    .string()
    .required("El nivel de influencia es obligatorio"),
  requirement: yup
    .string()
    .required("La necesidad es obligatoria")
    .max(1500, "Máximo 1500 caracteres"),
  expectative: yup
    .string()
    .required("La expectativa es obligatoria")
    .max(1500, "Máximo 1500 caracteres"),
  communication_mechanism: yup
    .string()
    .required("El mecanismo de comunicación es obligatorio")
    .max(500, "Máximo 500 caracteres"),
  accountability: yup
    .string()
    .required("La rendición de cuenta es obligatoria")
    .max(500, "Máximo 500 caracteres"),
  monitoring_sources: yup
    .string()
    .required("Las fuentes de monitoreo son obligatorias")
    .max(500, "Máximo 500 caracteres"),
  legal_requirement: yup.string().required("El requisito legal es obligatorio"),
  organization_requirement: yup
    .string()
    .required("El requisito de la organización es obligatorio"),
  customer_requirement: yup
    .string()
    .required("El requisito del cliente es obligatorio"),
  frequency: yup
    .string()
    .required("La frecuencia es obligatoria")
    .max(100, "Máximo 100 caracteres"),
  follow_up_comment: yup
    .string()
    .required("El comentario de seguimiento es obligatorio")
    .max(500, "Máximo 500 caracteres"),
  follow_up_date: yup.date().nullable().notRequired()
});

const CreateInterestedParties = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openVerification, setOpenVerification] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: ""
  });
  const { rowData, mode, loadData } = location.state || {};
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const userData = useSelector((state) => state.userData.userData);
  const canAccessProcess = canManageInterestedPartiesProcessRecords(
    userData?.roleList,
    loadData?.value
  );
  const effectiveViewMode = isViewMode || (isEditMode && !canAccessProcess);
  const effectiveFormDisabled = effectiveViewMode || !canAccessProcess;

  const processSelectOptions = useMemo(
    () =>
      loadData?.value != null && loadData?.name != null
        ? [{ name: loadData.name, value: loadData.value }]
        : [{ name: "—", value: "" }],
    [loadData]
  );

  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
     shouldFocusError: true
  });

  const handleOpenVerification = () => setOpenVerification(true);
  const handleCloseVerification = () => setOpenVerification(false);

  const saveCreateUpdate = async (formData) => {
    if (!canAccessProcess) {
      return;
    }
    const { process_display, ...rest } = formData;
    const processId =
      rowData?.process_id ?? loadData?.value ?? rowData?.value;
    const dataToSend = {
      ...rest,
      id: rowData?.id ?? 0,
      process_id: processId,
      user_creator_id: userData.id,
      type: "byProcess"
    };

    callApiAsync(
      () => postCreateInterestedParties(dataToSend),
      () => {
        setToastConfig({
          severity: "success",
          summary: "Acción realizada",
          detail: isEditMode
            ? "Se editó la parte interesada correctamente."
            : "Se creó la parte interesada con éxito."
        });
        setShowToast(true);
        setOpenVerification(false);

        navigate(
          "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail",
          { state: { rowData: loadData } }
        );
      }
    );
  };

  useEffect(() => {
    if (!loadData?.value) return;
    if (!Array.isArray(userData?.roleList)) return;
    if (!canAccessProcess) {
      if (isViewMode) {
        return;
      }
      if (isEditMode) {
        navigate(
          "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail/create",
          {
            replace: true,
            state: {
              ...location.state,
              mode: "view"
            }
          }
        );
        return;
      }
      navigate(
        "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail",
        {
          replace: true,
          state: { rowData: loadData }
        }
      );
    }
  }, [
    loadData,
    loadData?.value,
    isViewMode,
    isEditMode,
    canAccessProcess,
    userData?.roleList,
    navigate,
    location.state
  ]);

  const parseFollowUpDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    const s = String(v).split("T")[0];
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const mapInfluenceLevelForForm = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n < 1 || n > 3) return "";
    return n;
  };

  useEffect(() => {
    const processOptionValue = loadData?.value ?? rowData?.value ?? "";
    if (rowData?.id != null) {
      reset({
        process_display: processOptionValue,
        enter: rowData.enter ?? "",
        interested_parties: rowData.interested_parties ?? "",
        rol_type: rowData.rol_type ?? "",
        relation_type: rowData.relation_type ?? "",
        influence_type: rowData.influence_type ?? "",
        influence_level: mapInfluenceLevelForForm(rowData.influence_level),
        requirement: rowData.requirement ?? "",
        expectative: rowData.expectative ?? "",
        communication_mechanism: rowData.communication_mechanism ?? "",
        accountability: rowData.accountability ?? "",
        monitoring_sources: rowData.monitoring_sources ?? "",
        legal_requirement: rowData.legal_requirement ?? "",
        organization_requirement: rowData.organization_requirement ?? "",
        customer_requirement: rowData.customer_requirement ?? "",
        frequency: rowData.frequency ?? "",
        follow_up_comment: rowData.follow_up_comment ?? "",
        follow_up_date: parseFollowUpDate(rowData.follow_up_date)
      });
    } else {
      reset({
        process_display: processOptionValue,
        enter: "",
        interested_parties: "",
        rol_type: "",
        relation_type: "",
        influence_type: "",
        influence_level: "",
        requirement: "",
        expectative: "",
        communication_mechanism: "",
        accountability: "",
        monitoring_sources: "",
        legal_requirement: "",
        organization_requirement: "",
        customer_requirement: "",
        frequency: "",
        follow_up_comment: "",
        follow_up_date: null
      });
    }
  }, [rowData, loadData, reset]);

  return (
    <div>
      <BreadCrumbComp
        data={loadData}
        personalized={`Partes Interesadas - ${loadData?.name}`}
      />
      <div className="searchTitle">
        <h1>
          {isEditMode
            ? "Editar Parte Interesada"
            : isViewMode
            ? "Detalle Parte Interesada"
            : "Crear Parte Interesada"}
        </h1>
      </div>
      <div className="table-container">
        <h1 className="title-bio-details-chard vm-20">
          {isEditMode
            ? "Editar Parte Interesada"
            : isViewMode
            ? "Detalle Parte Interesada"
            : "Nueva Parte Interesada"}
        </h1>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={3}>
            <p className="create-label ml-0">Proceso:</p>
            <SimpleSelect
              name="process_display"
              control={control}
              disabled={true}
              options={processSelectOptions}
              margin="0"
              searchBar={false}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <p className="create-label ml-0">
              Entrada:<span className="red-text">*</span>
            </p>
            <SimpleSelect
              name="enter"
              control={control}
              error={errors.enter}
              disabled={effectiveFormDisabled}
              options={stakeholdersEntries}
              margin="0"
              searchBar={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <p className="create-label ml-0">
              Nombre de parte interesada:<span className="red-text">*</span>
            </p>
            <FormTextField
              name="interested_parties"
              control={control}
              error={errors.interested_parties}
              margin="0"
              disabled={effectiveFormDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <div className="HContainer">
              <p className="create-label ml-0">
                Tipo de rol:<span className="red-text">*</span>
              </p>
              <div className="pointer align-center">
                <StyledTooltip
                  title={
                    <>
                      <p className="tooltip_info_title">Tipo de Rol</p>
                      <div className="VContainer">
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Decisor:</span>{" "}
                          Tiene autoridad para aprobar o rechazar decisiones
                          clave.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Financiador:</span>{" "}
                          Aporta recursos económicos o materiales.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Ejecutor:</span>{" "}
                          Participa directamente en la implementación de
                          actividades.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">
                            Usuario final:
                          </span>{" "}
                          Recibe o utiliza el producto/ servicio del proyecto.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Regulador:</span>{" "}
                          Supervisa el cumplimiento de normas y leyes. 
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">
                            Influenciador:
                          </span>{" "}
                          Puede moldear opiniones o decisiones sin tener poder
                          formal.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Observador:</span>{" "}
                          No interviene directamente, pero está atento al
                          desarrollo del proyecto. 
                        </p>
                      </div>
                    </>
                  }
                  followCursor
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 10px", width: "30px" }}
                  />
                </StyledTooltip>
              </div>
            </div>
            <SimpleSelect
              name="rol_type"
              control={control}
              error={errors.rol_type}
              disabled={effectiveFormDisabled}
              options={stakeholdersRolType}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <div className="HContainer">
              <p className="create-label ml-0">
                Tipo de relación:<span className="red-text">*</span>
              </p>
              <div className="pointer align-center">
                <StyledTooltip
                  title={
                    <>
                      <p className="tooltip_info_title">Tipo de relación</p>
                      <div className="VContainer">
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Directa:</span> El
                          proveedor que entrega materiales esenciales para la
                          ejecución.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Indirecta:</span>{" "}
                          La comunidad que se beneficia del impacto ambiental
                          positivo del proyecto.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Formal:</span>{" "}
                          Contratos, convenios, acuerdos legales.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Informal:</span>{" "}
                          Influencia por reputación, redes sociales o vínculos
                          personales.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Permanente:</span>
                          Empleados de planta o socios estratégicos.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Temporal:</span>{" "}
                          Consultores, contratistas o voluntarios por proyecto.
                        </p>
                      </div>
                    </>
                  }
                  followCursor
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 10px", width: "30px" }}
                  />
                </StyledTooltip>
              </div>
            </div>

            <SimpleSelect
              name="relation_type"
              control={control}
              error={errors.relation_type}
              disabled={effectiveFormDisabled}
              options={stakeholderRelationselect}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <div className="HContainer">
              <p className="create-label ml-0">
                Tipo de influencia:<span className="red-text">*</span>
              </p>
              <div className="pointer align-center">
                <StyledTooltip
                  title={
                    <>
                      <p className="tooltip_info_title">Tipo de influencia</p>
                      <div className="VContainer">
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Esenciales:</span>{" "}
                          Alta influencia en la organización.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">Importantes:</span>{" "}
                          Influencia media en la organización.
                        </p>
                        <p className="tooltip_text">
                          <span className="tooltip_subtitle">
                            Cooperadores:
                          </span>{" "}
                          Baja influencia en la organización.
                        </p>
                      </div>
                    </>
                  }
                  followCursor
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 10px", width: "30px" }}
                  />
                </StyledTooltip>
              </div>
            </div>
            <SimpleSelect
              name="influence_type"
              control={control}
              error={errors.influence_type}
              disabled={effectiveFormDisabled}
              options={stakeholdersinfluence}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <div className="HContainer">
              <p className="create-label ml-0">
                Nivel de influencia:<span className="red-text">*</span>
              </p>
              <div className="pointer align-center">
                <StyledTooltip
                  title={
                    <>
                      <p className="tooltip_info_title">Nivel de influencia</p>
                      <div className="VContainer">
                        <p className="tooltip_text">Baja (1–2)</p>
                        <p className="tooltip_text">Media (3)</p>
                        <p className="tooltip_text">Alta (4–5)</p>
                      </div>
                    </>
                  }
                  followCursor
                >
                  <img
                    className="btn-add-icon"
                    src={icons.infoExclamation}
                    alt="icon"
                    style={{ margin: "0 0 7px 10px", width: "30px" }}
                  />
                </StyledTooltip>
              </div>
            </div>
            <SimpleSelect
              name="influence_level"
              control={control}
              error={errors.influence_level}
              disabled={effectiveFormDisabled}
              options={stakeholderlevel}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Necesidad:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="requirement"
                  control={control}
                  isTextArea={true}
                  error={errors.requirement}
                  maxLength={1500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"requirement"}
                  control={control}
                  maxLength={1500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Expectativa:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="expectative"
                  control={control}
                  error={errors.expectative}
                  isTextArea={true}
                  maxLength={1500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"expectative"}
                  control={control}
                  maxLength={1500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Mecanismo de comunicación:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="communication_mechanism"
                  control={control}
                  isTextArea={true}
                  error={errors.communication_mechanism}
                  maxLength={500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"communication_mechanism"}
                  control={control}
                  maxLength={500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Rendición de cuenta:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="accountability"
                  control={control}
                  error={errors.accountability}
                  isTextArea={true}
                  maxLength={500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"accountability"}
                  control={control}
                  maxLength={500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Fuentes de monitoreo:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="monitoring_sources"
                  control={control}
                  isTextArea={true}
                  error={errors.monitoring_sources}
                  maxLength={500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"monitoring_sources"}
                  control={control}
                  maxLength={500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Frecuencia de monitoreo:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="frequency"
                  control={control}
                  error={errors.frequency}
                  maxLength={100}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"frequency"}
                  control={control}
                  maxLength={100}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">
              Comentario de seguimiento:<span className="red-text">*</span>
            </p>
            <div>
              <div>
                <FormTextField
                  name="follow_up_comment"
                  control={control}
                  isTextArea={true}
                  error={errors.follow_up_comment}
                  maxLength={500}
                  margin={"0"}
                  disabled={effectiveFormDisabled}
                />
              </div>
              <div>
                <CharacterCounter
                  name={"follow_up_comment"}
                  control={control}
                  maxLength={500}
                />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <p className="create-label ml-0">Fecha de seguimiento:</p>
            <FormCalendar
              name="follow_up_date"
              control={control}
              label=""
              defaultValue={null}
              formLabel=""
              margin="0"
              disabled={effectiveFormDisabled}
              error={errors.follow_up_date}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <p className="create-label ml-0">
              Requisito legal:<span className="red-text">*</span>
            </p>

            <FormRadioButton
              name="legal_requirement"
              control={control}
              label=""
              options={YesNoOptionsGroup}
              error={errors.legal_requirement}
              flexible={true}
              disabled={effectiveFormDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <p className="create-label ml-0">
              Requisito de la organización:<span className="red-text">*</span>
            </p>
            <FormRadioButton
              name="organization_requirement"
              control={control}
              label=""
              options={YesNoOptionsGroup}
              error={errors.organization_requirement}
              flexible={true}
              disabled={effectiveFormDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <p className="create-label ml-0">
              Requisito del cliente:<span className="red-text">*</span>
            </p>
            <FormRadioButton
              name="customer_requirement"
              control={control}
              label=""
              options={YesNoOptionsGroup}
              error={errors.customer_requirement}
              flexible={true}
              disabled={effectiveFormDisabled}
            />
          </Grid>
        </Grid>
        {!effectiveFormDisabled && canAccessProcess && (
          <div className="HContainer j-center w-complete mt-40">
            <LaddaButton
              variant="contained"
            onClick={handleSubmit(() => setOpenVerification(true))}
            >
              <div className="HContainer align-center">
                <p className="button_text">
                  {isEditMode
                    ? "Editar Parte interesada"
                    : "Crear Parte interesada"}
                </p>
              </div>
            </LaddaButton>
          </div>
        )}
        <VerificationModal
          open={openVerification}
          handleClose={handleCloseVerification}
          alertText={
            <>
              <b>
                {isEditMode
                  ? "¿Estás seguro de guardar los cambios de la parte interesada?"
                  : "¿Estás seguro de crear la nueva parte interesada?"}
              </b>
            </>
          }
          onConfirm={handleSubmit(saveCreateUpdate)}
        />

        <ToastNotification
          severity={toastConfig.severity}
          summary={toastConfig.summary}
          detail={toastConfig.detail}
          showToast={showToast}
          setShowToast={setShowToast}
          position="top-right"
        />
      </div>
    </div>
  );
};

export default CreateInterestedParties;
